import { ref, onMounted, onBeforeUnmount } from '@vue/composition-api';

/**
 * @param {() => boolean} hasUnsavedChanges - getter returning whether there are
 *   unsaved changes right now; the guard prompts only when it returns true. A
 *   getter (not a plain boolean) so the value is re-read each time the guard
 *   fires, reflecting edits made after this composable was set up.
 * @param {() => boolean | Promise<boolean>} onConfirm - runs on the main
 *   action; resolve truthy to proceed with navigation, falsy to stay.
 */
export const useUnsavedChangesGuard = ({ hasUnsavedChanges, onConfirm }) => {
  const isPromptOpen = ref(false);
  const isProcessing = ref(false);

  // The pending router `next`, held while the prompt is open.
  let pendingNext = null;

  const resolveLeave = allow => {
    const next = pendingNext;
    pendingNext = null;
    isPromptOpen.value = false;

    if (next) next(allow);
  };

  const guardLeave = next => {
    if (!hasUnsavedChanges()) {
      next();
      return;
    }

    pendingNext = next;
    isPromptOpen.value = true;
  };

  const confirmLeave = async () => {
    if (isProcessing.value) return;

    isProcessing.value = true;

    let shouldLeave = true;
    try {
      shouldLeave = await onConfirm();
    } finally {
      isProcessing.value = false;
    }

    if (shouldLeave) resolveLeave();
  };

  const cancelLeave = () => resolveLeave(false);

  const handlePromptInput = isOpen => {
    if (!isOpen) cancelLeave();
  };

  // Hard exits the router can't intercept (refresh, tab close, address bar).
  const handleBeforeUnload = event => {
    if (!hasUnsavedChanges()) return;

    event.preventDefault();
    event.returnValue = '';
  };

  onMounted(() => window.addEventListener('beforeunload', handleBeforeUnload));
  onBeforeUnmount(() =>
    window.removeEventListener('beforeunload', handleBeforeUnload)
  );

  return {
    isPromptOpen,
    isProcessing,
    guardLeave,
    confirmLeave,
    cancelLeave,
    handlePromptInput,
  };
};
